import { useRef, useEffect, useState, useMemo, useId } from 'react';
import './CurvedLoop.css';

interface CurvedLoopProps {
  marqueeText?: string;
  speed?: number;
  className?: string;
  curveAmount?: number;
  direction?: 'left' | 'right';
  interactive?: boolean;
}

const CurvedLoop: React.FC<CurvedLoopProps> = ({
  marqueeText = '',
  speed = 2,
  className,
  curveAmount = 400,
  direction = 'left',
  interactive = true
}) => {
  const text = useMemo(() => {
    const hasTrailing = /\s|\u00A0$/.test(marqueeText);
    return (hasTrailing ? marqueeText.replace(/\s+$/, '') : marqueeText) + '\u00A0';
  }, [marqueeText]);

  const measureRef = useRef<SVGTextElement>(null);
  const textPathRef = useRef<SVGTextPathElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [spacing, setSpacing] = useState(0);
  const [offset, setOffset] = useState(0);
  const uid = useId();
  const pathId = `curve-${uid}`;
  const pathD = `M-100,40 Q500,${40 + curveAmount} 1540,40`;

  const dragRef = useRef(false);
  const lastXRef = useRef(0);
  const dirRef = useRef(direction);
  const velRef = useRef(0);

  const textLength = spacing;
  const totalText = textLength
    ? Array(Math.ceil(1800 / textLength) + 2)
        .fill(text)
        .join('')
    : text;
  const ready = spacing > 0;

  useEffect(() => {
    if (measureRef.current) setSpacing(measureRef.current.getComputedTextLength());
  }, [text, className]);

  useEffect(() => {
    if (!spacing) return;
    if (textPathRef.current) {
      const initial = -spacing;
      textPathRef.current.setAttribute('startOffset', initial + 'px');
      setOffset(initial);
    }
  }, [spacing]);

  useEffect(() => {
    dirRef.current = direction;
  }, [direction]);

  useEffect(() => {
    if (!spacing) return;
    let rafId: number;
    let lastTime = Date.now();

    const loop = () => {
      if (!textPathRef.current) return;
      const now = Date.now();
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      if (!dragRef.current) {
        const dir = dirRef.current === 'left' ? -1 : 1;
        velRef.current = dir * speed * 50;
      }

      const newOff = offset + velRef.current * dt;
      let normalized = ((newOff % spacing) + spacing) % spacing;
      if (normalized > 0) normalized -= spacing;

      setOffset(normalized);
      textPathRef.current.setAttribute('startOffset', normalized + 'px');
      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [spacing, speed, offset]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!interactive) return;
    dragRef.current = true;
    lastXRef.current = e.clientX;
    velRef.current = 0;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current || !interactive) return;
    const dx = e.clientX - lastXRef.current;
    lastXRef.current = e.clientX;
    velRef.current = dx * 2;
    if (Math.abs(velRef.current) > 5) {
      dirRef.current = velRef.current < 0 ? 'left' : 'right';
    }
  };

  const endDrag = () => {
    if (!interactive) return;
    dragRef.current = false;
  };

  const cursorStyle = interactive
    ? dragRef.current
      ? 'grabbing'
      : 'grab'
    : 'default';

  return (
    <div
      className="curved-loop-jacket"
      style={{ visibility: ready ? 'visible' : 'hidden', cursor: cursorStyle }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
    >
      <svg className="curved-loop-svg" viewBox="0 0 1440 120">
        <defs>
          <path ref={pathRef} id={pathId} d={pathD} />
        </defs>
        <text ref={measureRef} className={className} style={{ opacity: 0, pointerEvents: 'none' }}>
          {text}
        </text>
        <text className={className}>
          <textPath ref={textPathRef} href={`#${pathId}`} startOffset="0">
            {totalText}
          </textPath>
        </text>
      </svg>
    </div>
  );
};

export default CurvedLoop;
